import { css, html } from 'react-strict-dom';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useTimer, useTimerElapsed } from 'src/contexts';
import { Model } from 'src/models';
import { formatAsReadableDate, formatElapsed } from 'src/util';

import MaybeLink from './MaybeLink';
import Text from './Text';
import TimerButton from './TimerButton';
import TimerCompanyLogo from './TimerCompanyLogo';
import TimerDailyGoal from './TimerDailyGoal';
import TimerError from './TimerError';
import TimerRecentTask from './TimerRecentTask';

/** How many entries the quick switch row offers alongside the current one. */
const QUICK_SWITCH_LIMIT = 3;

type Props = {
  /** Minutes already logged against today's tickets, for the goal ring. */
  minutesToday: number;
};

/**
 * The dashboard timer: what is being tracked right now, how the day is going
 * against the logging goal, and one-tap switching to recent work.
 */
export default function TimerModule({ minutesToday }: Props) {
  const { running, runningTarget, lastTarget, lastCategory, logged } =
    useTimer();
  const elapsedSeconds = useTimerElapsed();

  // The running stretch isn't banked as minutes until it stops, so add it in
  // to keep the day's total moving while the clock does.
  const totalMinutes = minutesToday + Math.floor(elapsedSeconds / 60);

  // Whatever the header is already showing doesn't need repeating below it.
  const target = runningTarget ?? lastTarget;
  const seen = new Set<string>(target ? [target.iri] : []);
  const recents = logged
    .flatMap((entry) => {
      const recentTarget = Model.TimerTarget.fromWorkDone(entry);
      if (!recentTarget || seen.has(recentTarget.iri)) return [];
      seen.add(recentTarget.iri);
      return [{ entry, target: recentTarget }];
    })
    .slice(0, QUICK_SWITCH_LIMIT);

  // The lead-in is plain text; only the reference beside it opens the work.
  const metaLeadIn = runningTarget
    ? runningTarget.company?.name ?? null
    : logged[0]
    ? `Stopped ${formatAsReadableDate(logged[0].loggedAt)}`
    : null;

  return (
    <html.div style={styles.card}>
      <html.div style={styles.now}>
        <html.div style={styles.primary}>
          <html.div style={[Style.row, Style.alignCenter, styles.status]}>
            <html.span
              style={[
                Style.centerBoth,
                styles.live,
                running ? styles.liveRunning : styles.liveStopped,
              ]}
            >
              <html.span
                style={[
                  styles.dot,
                  running ? styles.dotRunning : styles.dotStopped,
                ]}
              />
            </html.span>
            <Text
              typeface="headline"
              color={running ? 'neutral100' : 'neutral40'}
              style={styles.elapsed}
            >
              {running ? formatElapsed(elapsedSeconds) : '--:--:--'}
            </Text>
          </html.div>

          <html.span style={styles.dividerPrimary} />

          <html.div style={styles.currentTask}>
            <html.div style={[Style.row, Style.alignCenter, styles.meta]}>
              {target && (
                <>
                  <TimerCompanyLogo
                    company={target.company}
                    title={target.title}
                  />
                  {metaLeadIn && (
                    <Text typeface="caption.medium" color="neutral60">
                      {target.reference ? `${metaLeadIn} ·` : metaLeadIn}
                    </Text>
                  )}
                  {target.reference && (
                    <MaybeLink to={target.path}>
                      <Text typeface="caption.medium" color="neutral60">
                        {target.reference}
                      </Text>
                    </MaybeLink>
                  )}
                </>
              )}
            </html.div>

            {/* The work keeps the space even when stopped — greyed, with the
                dashes and the "Stopped ..." line carrying that on their own. */}
            <MaybeLink to={target?.path ?? null} fill>
              <html.span style={styles.title}>
                <Text
                  typeface="body.medium"
                  color={runningTarget ? 'neutral100' : 'neutral60'}
                >
                  {target?.title ?? 'Not tracking anything'}
                </Text>
              </html.span>
            </MaybeLink>
          </html.div>
        </html.div>

        <html.div style={styles.secondary}>
          <html.span style={styles.dividerSecondary} />

          <TimerDailyGoal minutes={totalMinutes} />

          {target && (
            <TimerButton
              target={target}
              category={lastCategory}
              size="large"
              tone="solid"
              showLabel={!running}
              startLabel={
                target.reference ? `Start ${target.reference}` : 'Start'
              }
            />
          )}
        </html.div>
      </html.div>

      <TimerError style={styles.error} />

      {recents.length > 0 && (
        <>
          <html.span style={styles.rule} />
          <html.div style={styles.quickSwitch}>
            {recents.map(({ entry, target: recentTarget }) => (
              <TimerRecentTask
                key={entry.id}
                target={recentTarget}
                loggedAt={entry.loggedAt}
                category={entry.categoryString || null}
              />
            ))}
          </html.div>
        </>
      )}
    </html.div>
  );
}

const styles = css.create({
  card: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
    overflow: 'hidden',
  },
  now: {
    display: 'flex',
    paddingBlock: 16,
    paddingInline: 20,
    flexDirection: {
      default: 'row',
      '@media (max-width: 640px)': 'column',
    },
    alignItems: {
      default: 'center',
      '@media (max-width: 640px)': 'stretch',
    },
    gap: {
      default: 16,
      '@media (max-width: 640px)': 14,
    },
  },
  primary: {
    display: 'flex',
    flexGrow: 1,
    minWidth: 0,
    flexDirection: {
      default: 'row',
      '@media (max-width: 640px)': 'column',
    },
    alignItems: {
      default: 'center',
      '@media (max-width: 640px)': 'flex-start',
    },
    gap: {
      default: 16,
      '@media (max-width: 640px)': 12,
    },
  },
  status: {
    flexShrink: 0,
    gap: 16,
  },
  live: {
    display: 'flex',
    width: 20,
    height: 20,
    borderRadius: 999,
  },
  liveRunning: {
    backgroundColor: color.greenBg,
  },
  liveStopped: {
    backgroundColor: color.neutral10,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  dotRunning: {
    backgroundColor: color.green,
  },
  dotStopped: {
    backgroundColor: color.neutral40,
  },
  elapsed: {
    fontSize: 30,
    fontWeight: '600',
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    // Keeps the clock from jittering as the digits change under it.
    fontVariantNumeric: 'tabular-nums',
  },
  currentTask: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: 2,
    minWidth: 0,
    alignSelf: {
      default: 'auto',
      '@media (max-width: 640px)': 'stretch',
    },
  },
  meta: {
    gap: 6,
    minHeight: 20,
  },
  title: {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: {
      default: 1,
      '@media (max-width: 640px)': 2,
    },
  },
  secondary: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: 16,
    justifyContent: {
      default: 'flex-start',
      '@media (max-width: 640px)': 'space-between',
    },
  },
  dividerPrimary: {
    flexShrink: 0,
    width: 1,
    height: 36,
    backgroundColor: color.neutral20,
    display: {
      default: 'block',
      '@media (max-width: 640px)': 'none',
    },
  },
  dividerSecondary: {
    flexShrink: 0,
    width: 1,
    height: 36,
    backgroundColor: color.neutral20,
    display: {
      default: 'block',
      '@media (max-width: 640px)': 'none',
    },
  },
  rule: {
    height: 1,
    backgroundColor: color.neutral20,
  },
  error: {
    paddingInline: 20,
    paddingBottom: 16,
  },
  quickSwitch: {
    display: 'flex',
    gap: 8,
    paddingInline: 20,
    flexDirection: {
      default: 'row',
      '@media (max-width: 640px)': 'column',
    },
    alignItems: {
      default: 'center',
      '@media (max-width: 640px)': 'stretch',
    },
    paddingBlock: {
      default: 12,
      '@media (max-width: 640px)': 14,
    },
  },
});
